/**
 * Computed event status — display only, no stored field.
 *
 * Priority (highest first): Finished > Live > Upcoming > Pending payment > Draft
 *
 * Date rules:
 *   Upcoming = paid, today < first round date (start_date)
 *   Live     = today >= start_date AND (not all revealed OR scoring started) AND today <= end_date
 *   Finished = all rounds revealed, OR today > last round date (end_date)
 */

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function todayMidnight() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Is a single round finished? Same rule as the "Finished" branch in computeEventStatus:
 *   round.revealed === true, OR today > trip.end_date.
 *
 * @param {object} round - Round object (needs `revealed`)
 * @param {object} trip  - Trip/event object (needs `end_date`)
 * @returns {boolean}
 */
export function isRoundFinished(round, trip) {
  if (!round) return false;
  if (round.revealed) return true;
  const endDate = parseDate(trip?.end_date);
  if (endDate && todayMidnight() > endDate) return true;
  return false;
}

/**
 * @param {object} ev - Event/trip object
 * @param {Date} [overrideToday] - Override "today" for testing
 * @returns {{ label: string, color: string }} status badge info
 */
export function computeEventStatus(ev, overrideToday) {
  if (!ev) return { label: 'Unknown', color: 'bg-[#E1E6E4] text-[#455561]' };

  const isDraft = ev.status === 'draft';
  const hasPendingPayment = ev.latest_payment?.status === 'pending';
  const hasFailedPayment = ev.latest_payment?.status === 'failed';
  const isPaid = ev.status === 'paid' || ev.status === 'complimentary' || ev.status === 'owner_created';

  const rounds = ev.rounds || [];
  const allRevealed = rounds.length > 0 && rounds.every(r => r.revealed);

  const hasScoring = rounds.some(r => {
    // Server-computed flag (from /organiser/events trimmed payload)
    if (r.scoring_started != null) return r.scoring_started;
    // Server-computed summary from stripped trip response
    if (r.has_scores) return true;
    // Fallback for full trip data (TripDetail, Leaderboard, etc.)
    const hs = r.hole_strokes || {};
    const ths = r.team_hole_strokes || {};
    return Object.values(hs).some(a => Array.isArray(a) && a.some(v => v > 0))
      || Object.values(ths).some(a => Array.isArray(a) && a.some(v => v > 0));
  });

  const today = overrideToday || todayMidnight();
  const startDate = parseDate(ev.start_date);
  const endDate = parseDate(ev.end_date);

  // ── Finished: all rounds revealed, OR today > last round date ──
  if (isPaid && allRevealed && rounds.length > 0) {
    return { label: 'Finished', color: 'bg-[#E1E6E4] text-[#455561]' };
  }
  if (isPaid && endDate && today > endDate) {
    return { label: 'Finished', color: 'bg-[#E1E6E4] text-[#455561]' };
  }

  // ── Live: today >= start date OR scoring has started ──
  if (isPaid && (hasScoring || (startDate && today >= startDate))) {
    return { label: 'Live', color: 'bg-[#E8F5E9] text-[#2E7D32]' };
  }

  // ── Upcoming: paid, before start date (or no date set, no scoring) ──
  if (isPaid) {
    return { label: 'Upcoming', color: 'bg-[#E3F2FD] text-[#1565C0]' };
  }

  // ── Pending payment ──
  if (isDraft && hasPendingPayment) {
    return { label: 'Pending payment', color: 'bg-[#FFF3E0] text-[#E59000]' };
  }

  // ── Payment failed ──
  if (isDraft && hasFailedPayment) {
    return { label: 'Payment failed', color: 'bg-[#FFEBEE] text-[#C62828]' };
  }

  // ── Draft ──
  return { label: 'Draft', color: 'bg-[#E1E6E4] text-[#455561]' };
}
